import { Controller } from "stimulus";
import { subscribeToGroup } from "../channels/group_channel";

export default class extends Controller {
  static targets = ["messages", "latest", "connection"];

  connect() {
    this.unread = 0;
    this.syncTimelineDates();
    // La navigation n'a pas la même hauteur dans toutes les rooms.
    this.onViewportResize = () => this.updateViewportHeight();
    window.addEventListener("resize", this.onViewportResize);
    window.visualViewport?.addEventListener("resize", this.onViewportResize);
    this.updateViewportHeight();
    this.subscription = subscribeToGroup(this.messagesTarget, {
      connected: () => this.connectionChanged("connected", ""),
      disconnected: () => this.connectionChanged("disconnected", "Messagerie : reconnexion en cours…"),
      rejected: () => this.connectionChanged("disconnected", "Messagerie indisponible"),
      received: data => this.receive(data)
    });
    this.onImageLoad = () => {
      if (this.followingLatest) this.scrollToLatest();
    };
    this.messagesTarget.addEventListener("load", this.onImageLoad, true);
    this.resizeObserver = new ResizeObserver(() => {
      if (this.followingLatest) this.scrollToLatest();
    });
    this.resizeObserver.observe(this.messagesTarget);
    this.composerObserver = new ResizeObserver(entries => {
      // Réserver la place occupée par la saisie dans le défilement, sans changer
      // la hauteur du fil. Les derniers messages restent au-dessus de la saisie.
      const height = entries[0].target.getBoundingClientRect().height;
      this.element.style.setProperty("--chat-composer-height", `${height}px`);
      if (this.followingLatest) this.scrollToLatest();
    });
    this.composerObserver.observe(this.element.querySelector(".answering_form"));
    this.scrollToLatest();
  }

  disconnect() {
    window.removeEventListener("resize", this.onViewportResize);
    window.visualViewport?.removeEventListener("resize", this.onViewportResize);
    if (this.subscription) this.subscription.unsubscribe();
    this.resizeObserver.disconnect();
    this.composerObserver.disconnect();
    this.messagesTarget.removeEventListener("load", this.onImageLoad, true);
  }

  updateViewportHeight() {
    if (!this.element.getClientRects().length) return;
    const viewport = window.visualViewport;
    const bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const top = this.element.getBoundingClientRect().top;
    const layout = this.element.closest(".grid-channel-total") || this.element;
    layout.style.setProperty("--chat-viewport-height", `${Math.max(0, bottom - top)}px`);
  }

  connectionChanged(state, label) {
    this.connectionTarget.dataset.state = state;
    this.connectionTarget.textContent = label;
    this.connectionTarget.hidden = state === "connected";
  }

  trackScroll() {
    const list = this.messagesTarget;
    this.followingLatest = list.scrollHeight - list.scrollTop - list.clientHeight < 80;
    if (this.followingLatest) this.unread = 0;
    this.updateLatestButton();
  }

  scrollToLatest() {
    this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight;
    this.followingLatest = true;
    this.unread = 0;
    this.updateLatestButton();
  }

  updateLatestButton() {
    this.latestTarget.hidden = this.followingLatest;
    this.latestTarget.textContent = this.unread > 0
      ? `${this.unread} nouveau${this.unread > 1 ? "x" : ""} message${this.unread > 1 ? "s" : ""} ↓`
      : "Derniers messages ↓";
  }

  insertTimestamp(message, anchor, previous) {
    const timestamp = Number(message.dataset.messageTimestamp);
    if (!Number.isFinite(timestamp)) return;
    const dayChanged = previous && message.dataset.messageDate !== previous.dataset.messageDate;
    if (previous && !dayChanged) return;
    const divider = document.createElement("div");
    divider.className = "chat-date-divider";
    divider.dataset.chatDate = message.dataset.messageDate;
    const time = document.createElement("time");
    time.dateTime = new Date(timestamp * 1000).toISOString();
    time.textContent = message.dataset.dateLabel;
    divider.appendChild(time);
    anchor.before(divider);
  }

  syncTimelineDates() {
    const list = this.messagesTarget;
    list.querySelectorAll(".chat-date-divider").forEach(divider => divider.remove());
    let previous = null;
    list.querySelectorAll(":scope > .message-both-style").forEach(wrapper => {
      const message = wrapper.querySelector(":scope > .message_header[data-message-timestamp]");
      if (!message) return;
      this.insertTimestamp(message, wrapper, previous);
      previous = message;
      let previousReply = message;
      wrapper.querySelectorAll(".submessagejs[data-message-timestamp]").forEach(reply => {
        this.insertTimestamp(reply, reply, previousReply);
        previousReply = reply;
      });
    });
  }

  stagePending(event) {
    const { id, html, parentId } = event.detail;
    const message = document.createElement("div");
    message.id = `pending-${id}`;
    message.className = "message_header-container message--own message--pending";
    const total = document.createElement("div");
    total.className = "message-total";
    const row = document.createElement("div");
    row.style.display = "flex";
    const spacer = document.createElement("div");
    spacer.className = "chat-avatar-spacer";
    const authorContent = document.createElement("div");
    authorContent.className = "author-message_header-content";
    const author = document.createElement("div");
    author.className = "name-date-message_header user-name-chat";
    author.textContent = "Vous";
    const content = document.createElement("div");
    content.className = "trix-content";
    content.innerHTML = html; // HTML Trix assaini par DOMPurify dans le formulaire
    const status = document.createElement("div");
    status.className = "chat-pending-status";
    status.textContent = "Envoi…";
    authorContent.append(author, content, status);
    row.append(spacer, authorContent);
    total.appendChild(row);
    message.appendChild(total);
    if (parentId) {
      message.classList.add("submessagejs");
      let replies = this.messagesTarget.querySelector(`#messageheader${parentId}`);
      if (!replies) {
        replies = document.createElement("div");
        replies.id = `messageheader${parentId}`;
        this.messagesTarget.querySelector(`#message_header-${parentId}`).after(replies);
      }
      replies.appendChild(message);
      message.scrollIntoView({ block: "nearest" });
    } else {
      const wrapper = document.createElement("div");
      wrapper.className = "message-both-style";
      wrapper.appendChild(message);
      this.messagesTarget.appendChild(wrapper);
      this.scrollToLatest();
    }
    const empty = this.messagesTarget.querySelector(".chat-empty");
    if (empty) empty.remove();
  }

  receiveEvent(event) {
    this.receive(event.detail);
  }

  receive(data) {
    if (!this.element.isConnected) return;
    if (data.type === "reactions") {
      this.element.dispatchEvent(new CustomEvent("chat:reactions", { detail: data }));
      return;
    }
    const list = this.messagesTarget;
    const ownMessage = Number(data.current_user_id) === Number(list.dataset.userId);
    const prefix = data.type === "submessage" ? "submessage" : "message_header";

    if (data.message_to_delete) {
      const message = list.querySelector(`#${prefix}-${data.id_to_delete}`);
      if (!message) return;
      const content = message.querySelector(".trix-content");
      if (content) content.textContent = "Message supprimé";
      message.classList.add("message--deleted");
      const reactions = message.querySelector(".chat-reactions");
      if (reactions) reactions.remove();
      const actions = message.querySelector(".elements-to-answer-reply-destroy");
      if (actions) actions.remove();
      return;
    }

    const html = ownMessage ? data.body : (data.body_not_current_user || data.body);
    if (!html) return;
    const template = document.createElement("template");
    template.innerHTML = html.trim();
    const incoming = template.content.querySelector(`.${prefix === "submessage" ? "submessagejs" : "message_header"}`);
    if (!incoming) return;
    incoming.classList.toggle("message--own", Number(incoming.dataset.authorId) === Number(list.dataset.userId));
    const pending = ownMessage && data.client_message_id
      ? document.getElementById(`pending-${data.client_message_id}`) : null;
    const existing = list.querySelector(`#${incoming.id}`);
    if (existing) {
      const oldReactions = existing.querySelector(".chat-reactions");
      const newReactions = incoming.querySelector(".chat-reactions");
      if (oldReactions && newReactions && (oldReactions.dataset.reactionBusy ||
        Number(oldReactions.dataset.reactionsVersion) > Number(newReactions.dataset.reactionsVersion))) {
        newReactions.replaceWith(oldReactions);
      }
      if (pending) pending.remove();
      // Remplacer aussi la modale associée, sans supprimer les réponses du fil.
      const modal = template.content.querySelector(".modal");
      if (modal) {
        const oldModal = document.getElementById(modal.id);
        if (oldModal) oldModal.remove();
      }
      existing.replaceWith(template.content);
      this.syncTimelineDates();
      return;
    }
    if (pending) {
      pending.replaceWith(template.content);
      this.syncTimelineDates();
      if (this.followingLatest) this.scrollToLatest();
      return;
    }
    if (data.action === "update") return; // message ancien, pas encore chargé

    const follow = this.followingLatest || ownMessage;
    if (data.type === "submessage") {
      const parent = list.querySelector(`#message_header-${data.message_header_id}`);
      if (!parent) return;
      let replies = list.querySelector(`#messageheader${data.message_header_id}`);
      if (!replies) {
        replies = document.createElement("div");
        replies.id = `messageheader${data.message_header_id}`;
        parent.after(replies);
      }
      replies.appendChild(template.content);
      this.syncTimelineDates();
      if (ownMessage) {
        incoming.scrollIntoView({ block: "nearest" });
        this.trackScroll();
        return;
      }
    } else {
      const wrapper = document.createElement("div");
      wrapper.className = "message-both-style";
      wrapper.appendChild(template.content);
      list.appendChild(wrapper);
      this.syncTimelineDates();
    }
    const empty = list.querySelector(".chat-empty");
    if (empty) empty.remove();
    if (follow) this.scrollToLatest();
    else {
      this.unread += 1;
      this.updateLatestButton();
    }
  }
}
